/* eslint-disable no-console -- intentional console output for dev environment validation feedback */
import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export { DEFAULT_SIGN_IN_REDIRECT } from './constants/auth-defaults.mjs';

const isSkipValidation = Boolean(process.env.SKIP_ENV_VALIDATION);
const isDevelopment = process.env.NODE_ENV === 'development';

if (!isSkipValidation && isDevelopment) {
  console.log('\n🔍 Validating environment variables...\n');
}

export const env = createEnv({
  onValidationError: (error) => {
    const formatted = error.flatten().fieldErrors;
    const missing = Object.entries(formatted)
      .map(([key, errors]) => `  ❌ ${key}: ${errors?.join(', ')}`)
      .join('\n');

    console.error(
      '\n╔════════════════════════════════════════════════════════════════╗',
    );
    console.error(
      '║              MISSING ENVIRONMENT VARIABLES                     ║',
    );
    console.error(
      '╠════════════════════════════════════════════════════════════════╣',
    );
    console.error(
      '║ The following environment variables are missing or invalid:    ║',
    );
    console.error(
      '╚════════════════════════════════════════════════════════════════╝\n',
    );
    console.error(missing);
    console.error(
      '\n📝 Please check your .env file and ensure all required variables are set.',
    );
    console.error('   See apps/nsight-web/.env.example for reference.\n');

    throw new Error('Environment validation failed. See above for details.');
  },
  onInvalidAccess: (variable) => {
    throw new Error(
      `❌ Attempted to access server-side env variable "${variable}" on the client. ` +
        'Use the useEnvs() hook instead.',
    );
  },
  server: {
    NSIGHT_WEB_PORT: z.string().trim().min(1).default('3000'),
    AVATAR_READ_WRITE_TOKEN: z.string().trim().min(1),
    LOGOS_READ_WRITE_TOKEN: z.string().trim().min(1),
    CLERK_SECRET_KEY: z.string().trim().min(1),
    INVITE_WEBHOOK_SECRET: z.string().trim().min(1),
    ABLY_API_KEY: z.string().trim().min(1),
    EHR_BRIDGE_TOKEN: z.string().optional(),
    CRON_SECRET: z.string().length(16),
    DATADOG_APP_ID: z.string().optional(),
    ENV: z
      .enum(['dev', 'staging', 'demo', 'production', 'production-2'])
      .default('dev'),
    S3_BUCKET: z.string(),
    SYMPTOM_S3_BUCKET: z.string(),
    AVATAR_S3_BUCKET: z.string(),
    BRANDING_S3_BUCKET: z.string(),
    NSIGHT_URL: z.string().url().default('http://localhost:3000'),
    RSA_ENCRYPTION_PUBLIC_KEY: z.string(),
    CLERK_SIGN_IN_URL: z.string(),
    CLERK_SIGN_UP_URL: z.string(),
    CLERK_PUBLISHABLE_KEY: z.string().trim().min(1),
    INACTIVE_TIMEOUT_MS: z.string().optional().default('900000'),
    TRPC_GATEWAY_URL: z.string().url().default('http://localhost:5555'),
    TRANSCRIPTION_WEBSOCKET_URL: z
      .string()
      .url()
      .default('http://localhost:8282/'),
    DATADOG_APPLICATION_ID: z.string().uuid().optional(),
    DATADOG_CLIENT_TOKEN: z.string().optional(),
    DATADOG_SITE: z.string().default('datadoghq.com'),
    DATADOG_SERVICE: z.string().optional(),
    DATADOG_ENV: z.string().optional(),
    APP_VERSION: z.string().min(1).optional(),
    DATADOG_API_KEY: z.string().optional(),
    ENCRYPTION_KEY: z.string().min(1),
    USERFLOW_TOKEN: z.string(),
    RELEASE_TAG: z.string().optional(),
    DATADOG_RUM_SESSION_SAMPLE_RATE: z.string().optional(),
    DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE: z.string().optional(),
    COGNITO_AUTHORITY: z.string().url().optional(),
    COGNITO_CLIENT_ID: z.string().optional(),
    COGNITO_DOMAIN: z.string().optional(),
    COGNITO_REDIRECT_URI: z.string().url().optional(),
    COGNITO_POST_LOGOUT_REDIRECT_URI: z.string().url().optional(),
    CALL_CENTER_URL: z.string().url().default('http://localhost:5173'),
    USER_INVITE_EXPIRE_TIME: z.coerce.number().default(3600 * 8 * 1000), // 8hrs
    WEBSOCKET_TIMEOUT_MS: z.coerce.number().default(60 * 1000),
    ENABLE_COGNITO_ALL: z
      .enum(['true', 'false'])
      .default('false')
      .transform((v) => v === 'true'),
    FORCE_EMAIL_GATE_BYPASS_TO_CLERK: z
      .enum(['true', 'false'])
      .default('false')
      .transform((v) => v === 'true'),
  },

  client: {},

  runtimeEnv: {
    NSIGHT_WEB_PORT: process.env.NSIGHT_WEB_PORT,
    AVATAR_READ_WRITE_TOKEN: process.env.AVATAR_READ_WRITE_TOKEN,
    LOGOS_READ_WRITE_TOKEN: process.env.LOGOS_READ_WRITE_TOKEN,
    CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
    INVITE_WEBHOOK_SECRET: process.env.INVITE_WEBHOOK_SECRET,
    ABLY_API_KEY: process.env.ABLY_API_KEY,
    EHR_BRIDGE_TOKEN: process.env.EHR_BRIDGE_TOKEN,
    CRON_SECRET: process.env.CRON_SECRET,
    DATADOG_APP_ID: process.env.DATADOG_APP_ID,
    ENV: process.env.ENV,
    S3_BUCKET: process.env.S3_BUCKET,
    SYMPTOM_S3_BUCKET: process.env.SYMPTOM_S3_BUCKET,
    AVATAR_S3_BUCKET: process.env.AVATAR_S3_BUCKET,
    BRANDING_S3_BUCKET: process.env.BRANDING_S3_BUCKET,
    NSIGHT_URL: process.env.NSIGHT_URL,
    RSA_ENCRYPTION_PUBLIC_KEY: process.env.RSA_ENCRYPTION_PUBLIC_KEY,
    CLERK_SIGN_IN_URL: process.env.CLERK_SIGN_IN_URL,
    CLERK_SIGN_UP_URL: process.env.CLERK_SIGN_UP_URL,
    CLERK_PUBLISHABLE_KEY: process.env.CLERK_PUBLISHABLE_KEY,
    INACTIVE_TIMEOUT_MS: process.env.INACTIVE_TIMEOUT_MS,
    TRPC_GATEWAY_URL: process.env.TRPC_GATEWAY_URL,
    TRANSCRIPTION_WEBSOCKET_URL: process.env.TRANSCRIPTION_WEBSOCKET_URL,
    DATADOG_APPLICATION_ID: process.env.DATADOG_APPLICATION_ID,
    DATADOG_CLIENT_TOKEN: process.env.DATADOG_CLIENT_TOKEN,
    DATADOG_SITE: process.env.DATADOG_SITE,
    DATADOG_SERVICE: process.env.DATADOG_SERVICE,
    DATADOG_ENV: process.env.DATADOG_ENV,
    APP_VERSION: process.env.APP_VERSION,
    DATADOG_API_KEY: process.env.DATADOG_API_KEY,
    ENCRYPTION_KEY: process.env.ENCRYPTION_KEY,
    USERFLOW_TOKEN: process.env.USERFLOW_TOKEN,
    RELEASE_TAG: process.env.RELEASE_TAG,
    DATADOG_RUM_SESSION_SAMPLE_RATE:
      process.env.DATADOG_RUM_SESSION_SAMPLE_RATE,
    DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE:
      process.env.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
    COGNITO_AUTHORITY: process.env.COGNITO_AUTHORITY,
    COGNITO_CLIENT_ID: process.env.COGNITO_CLIENT_ID,
    COGNITO_DOMAIN: process.env.COGNITO_DOMAIN,
    COGNITO_REDIRECT_URI: process.env.COGNITO_REDIRECT_URI,
    COGNITO_POST_LOGOUT_REDIRECT_URI:
      process.env.COGNITO_POST_LOGOUT_REDIRECT_URI,
    CALL_CENTER_URL: process.env.CALL_CENTER_URL,
    USER_INVITE_EXPIRE_TIME: process.env.USER_INVITE_EXPIRE_TIME,
    WEBSOCKET_TIMEOUT_MS: process.env.WEBSOCKET_TIMEOUT_MS,
    ENABLE_COGNITO_ALL: process.env.ENABLE_COGNITO_ALL,
    FORCE_EMAIL_GATE_BYPASS_TO_CLERK:
      process.env.FORCE_EMAIL_GATE_BYPASS_TO_CLERK,
  },

  emptyStringAsUndefined: true,
  skipValidation: Boolean(process.env.SKIP_ENV_VALIDATION),
});

export const getClientEnv = () => ({
  CLERK_SIGN_IN_URL: env.CLERK_SIGN_IN_URL,
  CLERK_SIGN_UP_URL: env.CLERK_SIGN_UP_URL,
  CLERK_PUBLISHABLE_KEY: env.CLERK_PUBLISHABLE_KEY,
  INACTIVE_TIMEOUT_MS: env.INACTIVE_TIMEOUT_MS,
  TRPC_GATEWAY_URL: env.TRPC_GATEWAY_URL,
  TRANSCRIPTION_WEBSOCKET_URL: env.TRANSCRIPTION_WEBSOCKET_URL,
  DATADOG_APPLICATION_ID: env.DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN: env.DATADOG_CLIENT_TOKEN,
  DATADOG_SITE: env.DATADOG_SITE,
  DATADOG_SERVICE: env.DATADOG_SERVICE,
  DATADOG_ENV: env.DATADOG_ENV,
  APP_VERSION: env.APP_VERSION,
  USERFLOW_TOKEN: env.USERFLOW_TOKEN,
  RELEASE_TAG: env.RELEASE_TAG,
  DATADOG_RUM_SESSION_SAMPLE_RATE: env.DATADOG_RUM_SESSION_SAMPLE_RATE,
  DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE:
    env.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
  COGNITO_AUTHORITY: env.COGNITO_AUTHORITY,
  COGNITO_CLIENT_ID: env.COGNITO_CLIENT_ID,
  COGNITO_DOMAIN: env.COGNITO_DOMAIN,
  COGNITO_REDIRECT_URI: env.COGNITO_REDIRECT_URI,
  COGNITO_POST_LOGOUT_REDIRECT_URI: env.COGNITO_POST_LOGOUT_REDIRECT_URI,
  WEBSOCKET_TIMEOUT_MS: env.WEBSOCKET_TIMEOUT_MS,
  ENABLE_COGNITO_ALL: env.ENABLE_COGNITO_ALL,
  FORCE_EMAIL_GATE_BYPASS_TO_CLERK: env.FORCE_EMAIL_GATE_BYPASS_TO_CLERK,
});
